import { parseOutcomePrices } from '@/utils/market-outcomes';

/** @param {unknown} market */
export function marketVolume(market) {
  const vol = Number(market?.volume ?? market?.volume24hr ?? market?.volumeNum ?? 0);
  return Number.isFinite(vol) ? vol : 0;
}

/** 极低成交量 PM 子市场多为重复/脏数据 */
export const PM_MIN_LIST_VOLUME = 10_000;

/**
 * 从 PM 完整题目提取短标签（按文案脚本，而非仅 UI locale）。
 * @param {string} question
 * @param {string} [locale]
 */
export function extractShortLabelFromQuestion(question, locale = 'en') {
  const q = String(question || '').trim();
  if (!q) return '';

  // 题目本身是中文时走中文规则（切换到 en UI 但接口仍返回中文问句）
  if (/[\u4e00-\u9fff]/.test(q)) {
    return extractZhShortLabel(q);
  }

  if (String(locale).startsWith('zh')) {
    return extractZhShortLabel(q);
  }

  return extractEnShortLabel(q);
}

/**
 * PM multi-date 问句：… by June 30, 2026? → June 30（对齐 Gamma groupItemTitle）。
 * 仅认英文月份，避免误伤 “win by knockout” 等。
 * @param {string} q
 */
function extractEnByDateLabel(q) {
  const m = String(q || '').match(
    /\bby\s+((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2})(?:,?\s*\d{4})?\s*\??\s*$/i
  );
  if (!m?.[1]) return '';
  return m[1].replace(/\s+/g, ' ').trim();
}

/**
 * 英文短名：Will … win / Will … after…；以及 multi-date `by Month Day`。
 * @param {string} q
 */
function extractEnShortLabel(q) {
  const byDate = extractEnByDateLabel(q);
  if (byDate) return byDate;

  const willWin = q.match(/^Will\s+(.+?)\s+win\b/i);
  if (willWin?.[1]) return willWin[1].trim();

  const willGen = q.match(/^Will\s+(.+?)(?:\s+after\b|\s+following\b|\s*\?|$)/i);
  if (willGen?.[1]) {
    const core = trimEnTrailingContext(willGen[1]) || willGen[1].trim();
    if (core.length >= 2 && core.length <= 64) return core;
  }

  return trimEnTrailingContext(q) || q;
}

/** @param {string} text */
function trimEnTrailingContext(text) {
  return String(text || '')
    .replace(/\s+after\s+(?:the\s+)?.+$/i, '')
    .replace(/\s+following\s+(?:the\s+)?.+$/i, '')
    .replace(/[?\s]+$/g, '')
    .trim();
}

/** 过泛的谓语，图例/短名更适合用主语（队名等） */
const ZH_WEAK_PREDICATES = new Set(['赢', '获胜', '胜利', '胜出', '取胜', '输', '败', '失败']);

/**
 * 中文短名：优先「会/将…吗」的谓语核心，避免命中「会议」里的「会」。
 * 多 outcome 撞名由 resolvePolymarketDisplayLabels 兄弟差分兜底，此处不做话题特判。
 * @param {string} q
 */
function extractZhShortLabel(q) {
  // 「湖人能否夺冠」→ 队名在前；限制主语长度，避免吃掉整段状语
  const ableSubject = q.match(/^(.{1,16}?)(能否|会不会|是否会)/u);
  if (ableSubject?.[1]) {
    const subject = ableSubject[1].trim();
    if (subject && !/[，,。；;]/.test(subject)) return subject;
  }

  const predicate = trimZhLeadingContext(extractZhQuestionPredicate(q));
  if (
    predicate &&
    predicate.length >= 2 &&
    predicate.length <= 40 &&
    !ZH_WEAK_PREDICATES.has(predicate)
  ) {
    return predicate;
  }

  // 主语前置：某某会… → 某某（弱谓语时）
  const subjectMatch = q.match(/^(.+?)(能否|会不会|是否会|是否|将|会)/u);
  if (subjectMatch?.[1]) {
    const subject = subjectMatch[1].trim();
    const marker = subjectMatch[2];
    const markerAt = subjectMatch[1].length;
    if (marker === '会' && q.slice(markerAt, markerAt + 2) === '会议') {
      // 「会议」伪命中，跳过
    } else if (subject.length >= 1 && subject.length <= 40 && !/[，,]/.test(subject)) {
      return subject;
    }
  }

  return predicate || q;
}

/**
 * 去掉「在…会议后」等时间/场景状语，保留加息/降息/保持不变等动作核心。
 * 注意：日历截止「于2026年8月31日前达成」不得剥成「达成」（各 outcome 会撞名）。
 * @param {string} text
 */
export function trimZhLeadingContext(text) {
  let s = String(text || '').trim();
  if (!s) return s;

  const patterns = [
    /^在[^，,]{0,48}?(?:会议结束)?后/u,
    /^在[^，,]{0,48}?(?:会议上|会议中|会后)/u,
    // 仅相对/场景「于会前/于宣布后」；含年月日的截止日保留
    /^于(?![^，,]{0,48}?(?:年|月|日))[^，,]{0,48}?(?:后|前|时)/u,
    /^[^，,]{0,24}?会议结束后[，,]?/u,
  ];

  for (const re of patterns) {
    const next = s.replace(re, '').trim();
    if (next.length >= 2 && next.length < s.length) {
      s = next;
      break;
    }
  }

  // 利率上调50个基点 → 上调50个基点
  s = s.replace(/^利率(?=保持|上调|下调|加息|降息)/u, '').trim();
  return s || String(text || '').trim();
}

/**
 * 取问句末尾「会/将/能否…（吗）」中最后一次有效谓语。
 * 例：…利率会保持不变吗？ → 保持不变
 * @param {string} q
 */
function extractZhQuestionPredicate(q) {
  const cut = String(q || '')
    .trim()
    .replace(/(?:吗|么)?[？?。.\s]*$/u, '');
  if (!cut) return '';

  const markers = ['会不会', '能否', '是否会', '是否', '将', '会'];
  let bestIdx = -1;
  let bestLen = 0;

  for (const marker of markers) {
    let from = 0;
    while (from < cut.length) {
      const i = cut.indexOf(marker, from);
      if (i < 0) break;
      if (marker === '会' && cut.slice(i, i + 2) === '会议') {
        from = i + 2;
        continue;
      }
      if (i >= bestIdx) {
        bestIdx = i;
        bestLen = marker.length;
      }
      from = i + marker.length;
    }
  }

  if (bestIdx < 0) return '';
  return cut.slice(bestIdx + bestLen).trim();
}

const EN_MONTH_TO_ZH = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

/**
 * PM groupItemTitle 常见形态「June 30」/「August 31」→ 中文「6月30日」。
 * 已是中文或非日期短标则原样返回。
 * @param {string} short
 * @param {string} locale
 */
export function localizeGroupItemTitle(short, locale) {
  const s = String(short || '').trim();
  if (!s || !String(locale || '').startsWith('zh')) return s;
  if (/[\u4e00-\u9fff]/.test(s)) return s;

  const m = s.match(/^([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?$/i);
  if (!m) return s;
  const month = EN_MONTH_TO_ZH[m[1].toLowerCase()];
  if (!month) return s;
  return `${month}月${Number(m[2])}日`;
}

/**
 * 单条市场行标签（无兄弟上下文）。
 * 多 outcome 集合请用 resolvePolymarketDisplayLabels，以便缺短标时做兄弟差分。
 * @param {object|null|undefined} market
 * @param {string} [locale]
 */
export function resolvePolymarketDisplayLabel(market, locale = 'en') {
  const question = String(market?.question || '').trim();
  const short = String(market?.groupItemTitle || '').trim();
  const loc = String(locale || 'en');

  if (short) return localizeGroupItemTitle(short, loc);

  if (question) {
    const extracted = extractShortLabelFromQuestion(question, loc);
    if (extracted && extracted !== question) return extracted;
  }

  return question || '—';
}

/** @param {object} market */
export function polymarketOutcomeDedupeKey(market, locale = 'zh') {
  const title = String(market?.groupItemTitle || '')
    .trim()
    .toLowerCase();
  if (title) return `title:${title}`;
  const label = resolvePolymarketDisplayLabel(market, locale).trim().toLowerCase();
  if (label && label !== '—') return `label:${label}`;
  const cid = String(market?.conditionId || '').trim();
  if (cid) return `cid:${cid}`;
  return `q:${String(market?.question || '')
    .trim()
    .toLowerCase()}`;
}

/** 极低成交量 + 极端概率的 PM 子市场，多为重复脏数据（如西班牙 $126 / <1%） */
export function isPolymarketJunkOutcome(market) {
  const vol = marketVolume(market);
  if (vol >= PM_MIN_LIST_VOLUME) return false;

  const prices = parseOutcomePrices(market?.outcomePrices);
  if (prices.length < 2) return true;

  let yes = Number(prices[0]);
  if (!Number.isFinite(yes)) return true;
  if (yes > 1) yes /= 100;

  return yes < 0.02 || yes > 0.98;
}

/**
 * PM collection：去重 + 过滤极低成交量/脏数据，保留成交量最高的一条。
 * @param {object[]} markets
 * @param {string} [locale]
 */
export function dedupePolymarketListedMarkets(markets, locale = 'zh') {
  if (!Array.isArray(markets)) return [];

  const byKey = new Map();
  for (const market of markets) {
    if (isPolymarketJunkOutcome(market)) continue;

    const vol = marketVolume(market);
    if (vol < PM_MIN_LIST_VOLUME) continue;

    const key = polymarketOutcomeDedupeKey(market, locale);
    const prev = byKey.get(key);
    if (!prev || vol > marketVolume(prev)) {
      byKey.set(key, market);
    }
  }

  return [...byKey.values()];
}

/** @param {object[]} markets @param {string} [locale] */
export function filterPolymarketDisplayMarkets(markets, locale = 'zh') {
  return dedupePolymarketListedMarkets(markets, locale);
}
